import React from "react";
import { ChevronRight, X } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import PatternListField from "./PatternListField";
import QuickReplyListEditor from "./QuickReplyListEditor";
import { setOrDelete } from "./setOrDelete";

const PageContextCard = ({
  context,
  onChange,
  onRemove,
  isOpen,
  onToggle,
  index,
}) => {
  const { translate } = useLanguage();
  const ctx = context || {};

  const setField = (key, value) => onChange(setOrDelete(ctx, key, value));

  const patternCount = (ctx.urlPatterns || []).length;
  const replyCount = (ctx.quickReplies || []).length;
  const summary = `${patternCount} ${
    patternCount === 1
      ? translate("patternSingular") || "pattern"
      : translate("patternPlural") || "patterns"
  } · ${replyCount} ${
    replyCount === 1
      ? translate("replySingular") || "reply"
      : translate("replyPlural") || "replies"
  }`;

  return (
    <div className="rounded-lg overflow-hidden"
      style={{ border: "1px solid var(--color-border)" }}>
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 flex items-center gap-2 text-left"
          aria-expanded={isOpen}
        >
          <ChevronRight
            size={14}
            style={{
              color: "var(--color-text-muted)",
              transform: isOpen ? "rotate(90deg)" : "none",
              transition: "transform 120ms",
            }}
          />
          <span className="text-sm font-medium"
            style={{ color: "var(--color-text-primary)" }}>
            {ctx.name || `${translate("pageContext") || "Context"} ${index + 1}`}
          </span>
          {!isOpen && (
            <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              {summary}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={onRemove}
          aria-label={`${translate("removePageContext") || "Remove context"}: ${
            ctx.name || `${translate("pageContext") || "Context"} ${index + 1}`
          }`}
          className="p-1 rounded hover:bg-gray-100 transition-colors"
          style={{ color: "var(--color-text-muted)" }}
        >
          <X size={14} />
        </button>
      </div>

      {isOpen && (
        <div className="px-3 py-3 space-y-3"
          style={{ borderTop: "1px solid var(--color-border)" }}>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wide mb-2"
              style={{ color: "var(--color-text-primary)" }}>
              {translate("pageContextName") || "Name"}
            </label>
            <input
              type="text"
              value={ctx.name || ""}
              onChange={(e) => setField("name", e.target.value)}
              maxLength={40}
              className="field-input w-full"
              placeholder={translate("pageContextNamePlaceholder") || "e.g. Product"}
            />
          </div>

          <PatternListField
            value={ctx.urlPatterns}
            onChange={(v) => setField("urlPatterns", v)}
            label={translate("pageContextPatterns") || "URL patterns"}
            hint={
              translate("pageContextPatternsHint") ||
              "One pattern per line, each starting with /. * matches within a segment, ** crosses /."
            }
            placeholder={translate("pageContextPatternsPlaceholder") || "/produto/**"}
          />

          <div>
            <label className="block text-xs font-medium uppercase tracking-wide mb-2"
              style={{ color: "var(--color-text-primary)" }}>
              {translate("pageContextReplies") || "Quick replies"}
            </label>
            <QuickReplyListEditor
              values={ctx.quickReplies}
              onChange={(v) => setField("quickReplies", v)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PageContextCard;
